import React, { createContext, useContext, useState, useCallback } from 'react';
import { adminAPI } from '../api';

const AdminContext = createContext(null);

export function AdminProvider({ children }) {
  const [categories, setCategories] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load categories
  const loadCategories = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await adminAPI.getCategories();
      setCategories(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load categories');
      console.error('Failed to load categories:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load questions
  const loadQuestions = useCallback(async (categoryId = null) => {
    try {
      setLoading(true);
      setError(null);
      const response = await adminAPI.getQuestions(categoryId);
      setQuestions(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load questions');
      console.error('Failed to load questions:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load sessions
  const loadSessions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await adminAPI.getSessions();
      setSessions(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load sessions');
      console.error('Failed to load sessions:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Refresh all data
  const refreshAll = useCallback(async () => {
    await Promise.all([
      loadCategories(),
      loadQuestions(),
      loadSessions()
    ]);
  }, [loadCategories, loadQuestions, loadSessions]);

  const value = {
    // State
    categories,
    questions,
    sessions,
    loading,
    error,
    // Actions
    loadCategories,
    loadQuestions,
    loadSessions,
    refreshAll,
    // Setters for optimistic updates
    setCategories,
    setQuestions,
    setSessions,
  };

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
}

export function useAdmin() {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
}

